import { useEffect, useRef, type RefObject } from 'react';
import { API_BASE } from '../services/api';

export type PickerTab = 'emoji' | 'sticker';

export interface PickerSticker {
  id: number;
  image_url: string;
  pack_name?: string;
}

export interface StickerGroup {
  id: string;
  name: string;
  stickers: PickerSticker[];
}

interface ChatMediaPickerProps {
  isOpen: boolean;
  activeTab: PickerTab;
  onTabChange: (tab: PickerTab) => void;
  onClose: () => void;
  emojis: string[];
  stickerGroups: StickerGroup[];
  stickerError?: string | null;
  onEmojiSelect: (emoji: string) => void;
  onStickerSelect: (stickerId: number) => void;
  onManageStickers: () => void;
  outsideClickIgnoreRefs?: ReadonlyArray<RefObject<HTMLElement | null>>;
  returnFocusRef?: RefObject<HTMLElement | null>;
}

const pickerClassName =
  'absolute bottom-full left-3 sm:left-4 mb-2 z-50 flex h-[320px] max-h-[min(320px,60vh)] w-[360px] max-w-[calc(100vw-24px)] flex-col overflow-hidden rounded-2xl border border-gray-700 bg-[#1a1d24] shadow-2xl';

const tabClassName = 'flex-1 px-3 py-2 text-sm font-medium transition';

export default function ChatMediaPicker({
  isOpen,
  activeTab,
  onTabChange,
  onClose,
  emojis,
  stickerGroups,
  stickerError = null,
  onEmojiSelect,
  onStickerSelect,
  onManageStickers,
  outsideClickIgnoreRefs = [],
  returnFocusRef,
}: ChatMediaPickerProps) {
  const pickerRef = useRef<HTMLDivElement>(null);
  const emojiTabRef = useRef<HTMLButtonElement>(null);
  const stickerTabRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const activeTabRef = activeTab === 'emoji' ? emojiTabRef : stickerTabRef;
    activeTabRef.current?.focus();
  }, [activeTab, isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
        returnFocusRef?.current?.focus();
      }
    };

    const handleMouseDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (pickerRef.current?.contains(target)) return;
      if (outsideClickIgnoreRefs.some((ref) => ref.current?.contains(target))) return;
      onClose();
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleMouseDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleMouseDown);
    };
  }, [isOpen, onClose, outsideClickIgnoreRefs, returnFocusRef]);

  if (!isOpen) return null;

  return (
    <div
      ref={pickerRef}
      id="chat-media-picker"
      role="dialog"
      aria-label="Выбор эмодзи и стикеров"
      data-testid="chat-media-picker"
      className={pickerClassName}
    >
      <div className="flex border-b border-gray-700">
        <button
          ref={emojiTabRef}
          type="button"
          aria-pressed={activeTab === 'emoji'}
          onClick={() => onTabChange('emoji')}
          className={`${tabClassName} ${activeTab === 'emoji' ? 'text-white' : 'text-gray-500 hover:text-gray-300'}`}
        >
          Эмодзи
        </button>
        <button
          ref={stickerTabRef}
          type="button"
          aria-pressed={activeTab === 'sticker'}
          onClick={() => onTabChange('sticker')}
          className={`${tabClassName} ${activeTab === 'sticker' ? 'text-white' : 'text-gray-500 hover:text-gray-300'}`}
        >
          Стикеры
        </button>
      </div>

      <div data-testid="picker-scroll-area" className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
        {activeTab === 'emoji' ? (
          <div className="grid grid-cols-6 gap-1 p-3">
            {emojis.map((emoji) => (
              <button
                key={emoji}
                type="button"
                aria-label={emoji}
                onClick={() => onEmojiSelect(emoji)}
                className="flex h-11 items-center justify-center rounded-lg text-2xl transition hover:bg-gray-700"
              >
                {emoji}
              </button>
            ))}
          </div>
        ) : stickerError ? (
          <div role="alert" className="flex h-full items-center justify-center px-6 text-center text-red-300">
            {stickerError}
          </div>
        ) : stickerGroups.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center px-6 text-center text-gray-500">
            <p className="mb-2">У тебя пока нет стикеров</p>
            <button
              type="button"
              onClick={onManageStickers}
              className="text-sm font-bold text-blue-400 underline hover:text-blue-300"
            >
              Создать или добавить стикерпак →
            </button>
          </div>
        ) : (
          <div className="p-3">
            {stickerGroups.map((group) => (
              <section key={group.id} className="mb-4 last:mb-0">
                <h3 className="mb-2 text-xs font-bold uppercase tracking-wider text-gray-500">{group.name}</h3>
                <div className="grid grid-cols-5 gap-2 sm:grid-cols-6">
                  {group.stickers.map((sticker) => (
                    <button
                      key={sticker.id}
                      type="button"
                      aria-label={`Отправить стикер ${group.name} #${sticker.id}`}
                      title="Отправить стикер"
                      onClick={() => onStickerSelect(sticker.id)}
                      className="rounded-lg p-1 transition hover:scale-110 hover:bg-gray-800"
                    >
                      <div className="aspect-square overflow-hidden">
                        <img
                          src={`${API_BASE}${sticker.image_url}`}
                          alt={group.name}
                          className="h-full w-full object-contain"
                        />
                      </div>
                    </button>
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
